
<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15">
      <Form ref="testForm" @submit="generateRandomTest">
        <div class="row">
          <!-- Niveau -->
          <div class="col-md-4">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Niveau <span class="text-danger">*</span>
              </label>
              <Field name="niveau" v-slot="{ field }">
                <Multiselect
                  v-model="selectedNiveau"
                  :options="niveauOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  placeholder="Sélectionner un niveau"
                  @select="onNiveauChange"
                  v-bind="field"
                />
              </Field>
              <ErrorMessage name="niveau" class="text-danger" />
            </div>
          </div>

          <!-- Filière (dépend du niveau) -->
          <div class="col-md-4">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Filière
              </label>
              <Field name="filiere" v-slot="{ field }">
                <Multiselect
                  v-model="selectedFiliere"
                  :options="filiereOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  placeholder="Sélectionner une filière (facultatif)"
                  :disabled="!selectedNiveau || isLoadingFilieres"
                  @select="onFiliereChange"
                  v-bind="field"
                />
              </Field>
            </div>
          </div>

          <!-- Matière (dépend du niveau + filière) -->
          <div class="col-md-4">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Matière <span class="text-danger">*</span>
              </label>
              <Field name="matiere" v-slot="{ field }">
                <Multiselect
                  v-model="selectedMatiere"
                  :options="matiereOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  placeholder="Sélectionner une matière"
                  :disabled="!selectedNiveau || isLoadingMatieres"
                  v-bind="field"
                />
              </Field>
              <ErrorMessage name="matiere" class="text-danger" />
            </div>
          </div>
        </div>

        <div class="col-md-12 mt-5">
          <div class="d-flex align-items-center">
            <button
              type="submit"
              class="btn btn-info me-3"
              :disabled="isGenerating || !selectedNiveau || !selectedMatiere"
            >
              <span v-if="isGenerating">Génération...</span>
              <span v-else>Nouveau test</span>
            </button>
            <router-link to="/liste-sessions" class="btn btn-danger">
              Annuler
            </router-link>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import ApiService from '@/services/ApiService';
import { success, error } from '@/utils/utils';
import Multiselect from '@vueform/multiselect';
import { useRouter } from 'vue-router';

export default defineComponent({
  name: 'FaireTestQCM',
  components: { Form, Field, ErrorMessage, Multiselect },
  setup() {
    const router = useRouter();

    // États
    const selectedNiveau = ref<number | null>(null);
    const selectedFiliere = ref<number | null>(null);
    const selectedMatiere = ref<number | null>(null);
    const isLoadingFilieres = ref(false);
    const isLoadingMatieres = ref(false);
    const isGenerating = ref(false);

    // Options
    const niveauOptions = ref<{ value: number; label: string }[]>([]);
    const filiereOptions = ref<{ value: number; label: string }[]>([]);
    const matiereOptions = ref<{ value: number; label: string }[]>([]);

    // Validation
    const testSchema = Yup.object().shape({
      niveau: Yup.number().required('Le niveau est obligatoire').nullable(),
      matiere: Yup.number().required('La matière est obligatoire').nullable(),
      filiere: Yup.number().nullable(),
    });

    // Chargement initial des niveaux
    const loadNiveaux = async () => {
      try {
        const { data } = await ApiService.get('/all/niveaux');
        niveauOptions.value = data.data.data.map((n: any) => ({
          value: n.id,
          label: n.libelle,
        }));
      } catch (err: any) {
        error('Erreur chargement des niveaux');
      }
    };

    // Quand on change de niveau
    const onNiveauChange = async (niveauId: number) => {
      selectedNiveau.value = niveauId;
      selectedFiliere.value = null;
      selectedMatiere.value = null;
      filiereOptions.value = [];
      matiereOptions.value = [];

      if (niveauId) {
        await Promise.all([
          loadFilieresParNiveau(niveauId),
          loadMatieresParNiveau(niveauId),
        ]);
      }
    };

    // Charger les filières liées au niveau
    const loadFilieresParNiveau = async (niveauId: number) => {
      isLoadingFilieres.value = true;
      try {
        const { data } = await ApiService.get(`/filiereNiveauMatieres?niveau=${niveauId}&limit=0`);
        const filieresUniques = Array.from(
          new Set(data.data.data.map((fnm: any) => fnm.filiere?.id).filter(Boolean))
        ).map(id => data.data.data.find((fnm: any) => fnm.filiere?.id === id).filiere);

        filiereOptions.value = filieresUniques.map((f: any) => ({
          value: f.id,
          label: f.libelle,
        }));
      } catch (err) {
        filiereOptions.value = [];
      } finally {
        isLoadingFilieres.value = false;
      }
    };

    // Charger les matières (avec ou sans filière)
    const loadMatieresParNiveau = async (niveauId: number, filiereId?: number | null) => {
      isLoadingMatieres.value = true;
      try {
        let url = `/filiereNiveauMatieres?niveau=${niveauId}&limit=0`;
        if (filiereId) url += `&filiere=${filiereId}`;

        const { data } = await ApiService.get(url);
        const matieresUniques = Array.from(
          new Set(data.data.data.map((fnm: any) => fnm.matiere.id))
        ).map(id => data.data.data.find((fnm: any) => fnm.matiere.id === id).matiere);

        matiereOptions.value = matieresUniques.map((m: any) => ({
          value: m.id,
          label: m.libelle,
        }));
      } catch (err) {
        matiereOptions.value = [];
      } finally {
        isLoadingMatieres.value = false;
      }
    };

    // Quand on change de filière → recharge les matières filtrées
    const onFiliereChange = async (filiereId: number | null) => {
      selectedFiliere.value = filiereId;
      selectedMatiere.value = null;
      if (selectedNiveau.value) {
        await loadMatieresParNiveau(selectedNiveau.value, filiereId || undefined);
      }
    };

    // Génération du test
    const generateRandomTest = async () => {
      if (!selectedNiveau.value || !selectedMatiere.value) return;

      isGenerating.value = true;

      const payload: any = {
        niveauId: selectedNiveau.value,
        matiereId: selectedMatiere.value,
        filiereId: selectedFiliere.value || null,
        count: 20,
      };

      console.log('Envoi vers /random-questions :', payload);

      try {
        const { data } = await ApiService.post('/random-questions', payload);
        // ou juste 'random-questions' si ton ApiService ajoute déjà /api

        if (data.code === 200 && data.data.questions?.length > 0) {
          localStorage.setItem('testQuestions', JSON.stringify(data.data.questions));
          localStorage.setItem('testSessionId', String(data.data.sessionId));

          success('Test généré avec succès !');

          router.push({
            name: 'TestPage',
            query: {
              sessionId: data.data.sessionId,
              mode: 'new',
            },
          });
        }
      } catch (err: any) {
        console.error(err);
        error(err.response?.data?.message || 'Erreur lors de la génération du test');
      } finally {
        isGenerating.value = false;
      }
    };

    onMounted(() => {
      loadNiveaux();
    });

    return {
      testSchema,
      selectedNiveau,
      selectedFiliere,
      selectedMatiere,
      niveauOptions,
      filiereOptions,
      matiereOptions,
      isLoadingFilieres,
      isLoadingMatieres,
      isGenerating,
      onNiveauChange,
      onFiliereChange,
      generateRandomTest,
    };
  },
});
</script>

<style scoped>
.cursor-not-allowed {
  cursor: not-allowed;
  opacity: 0.6;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.progress {
  border-radius: 0;
  background-color: #e9ecef;
}

.progress-bar {
  transition: width 0.3s ease;
}
</style>